import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Plus, FileText, Edit, Trash2, Eye } from 'lucide-react'
import { Work, WorkType, WorkStatus } from '@/types'

const workTypeLabels: Record<WorkType, string> = {
  [WorkType.POEM]: 'Poetry',
  [WorkType.SHORT_STORY]: 'Short Story',
  [WorkType.ESSAY]: 'Essay',
  [WorkType.NOVEL]: 'Novel',
  [WorkType.FLASH_FICTION]: 'Flash Fiction'
}

const statusColors: Record<WorkStatus, string> = {
  [WorkStatus.DRAFT]: 'bg-gray-100 text-gray-800',
  [WorkStatus.READY]: 'bg-blue-100 text-blue-800',
  [WorkStatus.SUBMITTED]: 'bg-yellow-100 text-yellow-800',
  [WorkStatus.PUBLISHED]: 'bg-green-100 text-green-800'
}

export default function Works() {
  const [works, setWorks] = useState<Work[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<WorkType | 'all'>('all')

  useEffect(() => {
    // TODO: Fetch works from API
    const mockWorks: Work[] = [
      {
        id: '1',
        userId: 'user1',
        title: 'Ocean Waves',
        type: WorkType.POEM,
        content: 'Sample poem content...',
        wordCount: 45,
        status: WorkStatus.READY,
        createdAt: '2024-01-15T10:00:00Z',
        updatedAt: '2024-01-15T10:00:00Z'
      },
      {
        id: '2',
        userId: 'user1',
        title: 'The Last Train',
        type: WorkType.SHORT_STORY,
        content: 'Sample story content...',
        wordCount: 2500,
        status: WorkStatus.SUBMITTED,
        createdAt: '2024-01-10T10:00:00Z',
        updatedAt: '2024-01-10T10:00:00Z'
      }
    ]
    setWorks(mockWorks)
    setLoading(false)
  }, [])

  const filteredWorks = works.filter(work => 
    filter === 'all' || work.type === filter
  )

  if (loading) {
    return <div className="text-center py-8">Loading works...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">My Works</h1>
        <Link
          to="/works/new"
          className="btn btn-primary flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>New Work</span>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex space-x-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-md text-sm font-medium ${
            filter === 'all'
              ? 'bg-primary-100 text-primary-700'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All Works
        </button>
        {Object.entries(workTypeLabels).map(([type, label]) => (
          <button
            key={type}
            onClick={() => setFilter(type as WorkType)}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              filter === type
                ? 'bg-primary-100 text-primary-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Works Grid */}
      {filteredWorks.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No works</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating a new literary work.
          </p>
          <div className="mt-6">
            <Link to="/works/new" className="btn btn-primary">
              Create New Work
            </Link>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredWorks.map((work) => (
            <div key={work.id} className="card">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                  {work.title}
                </h3>
                <span className={`px-2 py-1 text-xs font-medium rounded ${statusColors[work.status]}`}>
                  {work.status}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Type:</span> {workTypeLabels[work.type]}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Word Count:</span> {work.wordCount.toLocaleString()}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Updated:</span>{' '}
                  {new Date(work.updatedAt).toLocaleDateString()}
                </p>
              </div>

              <div className="text-sm text-gray-700 mb-4 line-clamp-3">
                {work.content.substring(0, 150)}...
              </div>

              <div className="flex justify-between items-center">
                <div className="flex space-x-2">
                  <Link
                    to={`/works/${work.id}`}
                    className="text-primary-600 hover:text-primary-900"
                    title="View/Edit"
                  >
                    <Eye size={18} />
                  </Link>
                  <Link
                    to={`/works/${work.id}`}
                    className="text-gray-600 hover:text-gray-900"
                    title="Edit"
                  >
                    <Edit size={18} />
                  </Link>
                  <button
                    className="text-red-600 hover:text-red-900"
                    title="Delete"
                    onClick={() => {
                      if (confirm('Are you sure you want to delete this work?')) {
                        // TODO: Delete work
                      }
                    }}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                {work.submissions && work.submissions.length > 0 && (
                  <span className="text-xs text-gray-500">
                    {work.submissions.length} submission{work.submissions.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}